import * as React from 'react';
import { styled } from '@linaria/react';

import { normalize } from '@/utils';

const WHEEL_HEIGHT = 46;
const WHEEL_WIDTH = 12;
const NOTCH_HEIGHT = 12;

type ControlType = 'pitch' | 'mod';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  // Valid range: -1 to 1.
  value: number;
  min: number;
  max: number;
  label: string;
  controlType: ControlType;
  layers: number;
}

function KbdWheel({
  value,
  min,
  max,
  label,
  controlType,
  layers,
  ...delegated
}: Props) {
  const maxNotchShift = WHEEL_HEIGHT / 2 - NOTCH_HEIGHT / 2;

  const pitchNotchShift = normalize(
    value,
    min,
    max,
    maxNotchShift,
    maxNotchShift * -1
  );

  return (
    <Wrapper
      {...delegated}
      data-hide-inner-shadow={layers < 1}
      data-hide-grooves={layers < 4}
      data-hide-traintracks={layers < 3}
      data-hide-texture={layers < 2}
      data-hide-outline={layers < 3}
    >
      {layers > 7 && <Shadow />}

      <ControlButton>
        <ClickExpander />
        {layers > 4 && (
          <Notch
            style={{
              transform: `translateY(${pitchNotchShift}px)`,
              '--top-gradient-opacity': normalize(
                value,
                min,
                max,
                1,
                0.25
              ),
              '--bottom-gradient-opacity': normalize(
                value,
                min,
                max,
                0.6,
                1
              ),
            }}
          >
            {layers > 5 && (
              <NotchHighlight
                style={{
                  '--shift':
                    normalize(
                      value,
                      min,
                      max,
                      NOTCH_HEIGHT * 0.6,
                      NOTCH_HEIGHT * -0.4
                    ) +
                    1 +
                    'px',
                }}
              />
            )}
          </Notch>
        )}
      </ControlButton>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  --gap: 2px;
  position: relative;
  width: calc(${WHEEL_WIDTH}px + 2 * var(--gap));
  height: calc(${WHEEL_HEIGHT}px + 2 * var(--gap));
  padding: var(--gap);
  outline: 1px solid hsl(275deg 40% 80% / 0.4);
  outline-offset: 2px;
  border-radius: 2px;
  background: hsl(275deg 60% 15%);
  box-shadow:
    inset 0px 1px 2px hsl(275deg 60% 5%),
    inset 0px 2px 4px hsl(275deg 60% 5%);

  &:has(*:focus-visible) {
    outline: 2px solid white;
    outline-offset: 2px;
  }

  &[data-hide-inner-shadow='true'] {
    box-shadow: none;
  }
  &[data-hide-outline='true'] {
    outline: none;
  }
`;

const ControlButton = styled.div`
  --grooves: repeating-linear-gradient(
    to top,
    transparent 0px 1px,
    hsl(275deg 15% 6% / 0.25) 1px 2px
  );
  --train-track-left: linear-gradient(
    to right,
    transparent 0px 2px,
    hsl(275deg 30% 60% / 0.5) 2px 3px,
    transparent 3px
  );
  --train-track-right: linear-gradient(
    to left,
    transparent 0px 2px,
    hsl(275deg 30% 60% / 0.5) 2px 3px,
    transparent 3px
  );
  --base-texture: linear-gradient(
    to bottom,
    hsl(275deg 30% 20%),
    hsl(275deg 40% 40%) 28%,
    hsl(275deg 30% 20%)
  );

  ${Wrapper}[data-hide-grooves='true'] & {
    --grooves: linear-gradient(to right, transparent, transparent);
  }
  ${Wrapper}[data-hide-texture='true'] & {
    --base-texture: linear-gradient(
      to right,
      transparent,
      transparent
    );
  }

  ${Wrapper}[data-hide-traintracks='true'] & {
    --train-track-left: linear-gradient(
      to right,
      transparent,
      transparent
    );
    --train-track-right: linear-gradient(
      to right,
      transparent,
      transparent
    );
  }

  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 0;
  border: none;
  border-radius: 1px;
  /* prettier-ignore */
  background:
    /* TOP gradient: single horizontal lines added to give the wheel the sense of texture and grip. */
    var(--grooves),
    /* Next two gradients are the light lines running along the left/right edges of the wheel. */
    var(--train-track-left),
    var(--train-track-right),
    /* Finally, the base texture, which is a gradient that is lighter near the top */
    var(--base-texture);
  cursor: pointer;

  &:focus-visible {
    outline: none;
  }
`;

const ClickExpander = styled.span`
  position: absolute;
  inset: -6px;
`;

const Notch = styled.span`
  position: relative;
  display: flex;
  width: 100%;
  height: ${NOTCH_HEIGHT}px;
  background: linear-gradient(
      to bottom,
      hsl(275deg 15% 6% / var(--top-gradient-opacity)),
      hsl(275deg 15% 6% / 0)
    ),
    linear-gradient(
      to top,
      hsl(275deg 15% 6% / var(--bottom-gradient-opacity)),
      hsl(275deg 15% 6% / 0)
    );
  overflow: clip;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 1px;
    background: hsl(275deg 30% 80% / 0.25);
  }
`;

const NotchHighlight = styled.div`
  flex: 1;
  background: linear-gradient(
    to bottom,
    hsl(275deg 20% 90% / 0) 15%,
    hsl(275deg 20% 80% / 1) 50%,
    hsl(275deg 20% 90% / 0) 85%
  );
  opacity: 0.2;
  transform: translateY(var(--shift));
`;

const Shadow = styled.div`
  position: absolute;
  top: 2px;
  right: 0;
  bottom: 2px;
  width: 8px;
  border-radius: 0 50% 50% 0;
  background: hsl(275deg 80% 12%);
  transform: translateX(50%);
  filter: blur(3px);
  opacity: 0.75;
`;

export default React.memo(KbdWheel);
